import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Alert,
  Divider,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import authService from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../services/apiConfig';
import axios from 'axios';
import { Footer } from '../components/layout/Footer/footer';
import GoogleSignIn from '../components/auth/GoogleSignIn';
import Navbar from '../components/layout/Navbar/Navbar';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('You must agree to the Terms and Privacy Policy');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        formData
      );

      await login(response.data);
      navigate('/dashboard/generate-post');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleStart = () => {
    setGoogleLoading(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    
    if (error === 'auth_failed') {
      setError('Authentication failed. Please try again.');
    } else if (error === 'email_conflict') {
      setError('This email is already linked to a different Google account.');
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Login | Instation</title>
        <meta name="description" content="Log in to your Instation account to manage your LinkedIn content and engagement." />
      </Helmet>
      
      <Navbar />
      
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        pt: { xs: 8, sm: 8 },
        '& .auth-paper': {
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }
        },
        '& .auth-title': {
          fontWeight: 600,
          mb: 3,
          textAlign: 'center',
          fontSize: { xs: '1.75rem', sm: '2rem' },
          background: 'linear-gradient(45deg, #458EFF, #3672FF)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'fadeIn 0.6s ease-out',
          fontFamily: 'Poppins'
        },
        '& .auth-form-field': {
          mb: 2,
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#458EFF'
              }
            },
            '&.Mui-focused': {
              transform: 'translateY(-2px)'
            }
          }
        },
        '& .auth-submit-button': {
          mt: 3,
          height: 48,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '16px',
          fontFamily: 'Poppins',
          fontWeight: 600,
          bgcolor: '#458EFF',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: '#3672FF',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(69, 142, 255, 0.2)'
          },
          '&:disabled': {
            bgcolor: '#e0e0e0',
            transform: 'none'
          }
        },
        '& .auth-alternate-button': {
          mt: 2,
          color: 'text.secondary',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#458EFF',
            bgcolor: 'rgba(69, 142, 255, 0.08)'
          }
        },
        '& .auth-divider-text': {
          px: 2,
          color: 'text.secondary',
          animation: 'fadeIn 0.8s ease-out'
        },
        '& .forgot-password-link': {
          color: '#458EFF',
          textDecoration: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#3672FF',
            textDecoration: 'underline'
          }
        },
        '@keyframes fadeIn': {
          from: {
            opacity: 0,
            transform: 'translateY(10px)'
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}>
        <Container maxWidth="sm" sx={{ flex: 1, py: { xs: 4, sm: 8 } }}>
          <Paper className="auth-paper">
            <Typography 
              variant="h4" 
              className="auth-title"
            >
              Welcome Back
            </Typography>

            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3 }} 
                onClose={() => setError('')}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <GoogleSignIn onStartAuth={handleGoogleStart} />
            </Box>

            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <Divider sx={{ flex: 1 }} />
              <Typography 
                variant="body2" 
                className="auth-divider-text"
              >
                or login with email
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="auth-form-field"
                disabled={loading}
              />
              
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="auth-form-field"
                disabled={loading}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  className="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox 
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link 
                      href="https://instation.app/terms-and-condition" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="forgot-password-link"
                    >
                      Terms and Conditions
                    </Link>
                    {' '}and{' '}
                    <Link 
                      href="https://instation.app/privacy" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="forgot-password-link"
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || googleLoading}
                className="auth-submit-button"
              >
                {(loading || googleLoading) ? (
                  <>
                    <CircularProgress 
                      size={20} 
                      sx={{ 
                        color: 'white',
                        position: 'absolute',
                        left: '50%',
                        marginLeft: '-10px'
                      }} 
                    />
                    <span style={{ opacity: 0 }}>Log In</span>
                  </>
                ) : 'Log In'}
              </Button>
            </form>

            <Button
              variant="text"
              fullWidth
              onClick={() => navigate('/signup')}
              disabled={loading}
              className="auth-alternate-button"
            >
              Don't have an account? Sign up
            </Button>
          </Paper>
        </Container>
        <Footer />
      </Box>
    </>
  );
};

export default Login;